/**
 * @file 页面配置整合（如何后期接口改成是配置的话）
 */
import { isClient } from '@vueuse/shared';
import { $api, ip, baseAPI } from '@/config/api';
import { i18n, getLanguage } from '@/lang/i18n';
import axios from '@/utils/axios';
import { toFormat } from '@/utils/number';

import moment from 'moment-timezone';
import siteConfig from '@/config/site';
import { useCrypto } from '@/utils/crypto';
import { getCookie } from '@/utils/cookie';
import { getTdidDeviceId } from '@/utils/deviceToken';

//  interface Children {
//    blank: boolean;
//    link: string;
//    nofollow: boolean;
//    text: string;
//  }
//  interface Header {
//    blank: boolean;
//    children: Children[];
//    link: string;
//    nofollow: boolean;
//    status: boolean;
//    system: boolean;
//    text: string;
//  }

export const useConfigStore = defineStore('config', {
  state: () => ({
    // 计价货币
    // quoteTokens: <Array<any>>[],
    realtimeInterval: '24h',
    // 最新成交展示条数
    lastTradesMax: 50,
    // 全局配置
    config: <Record<string, any>>{},
    // 页面入口隐藏配置
    hideConfig: <Record<string, any>>{},
    // 首页配置数据
    // homeConfig: <Record<string, any>>{
    //   announcements: []
    // },
    theme: isClient ? (localStorage.getItem('theme') || 'wh-theme') : 'wh-theme',
    // 是否已请求配置
    isFetchConfig: false,
    homeConfigReady: <any>{},
    localeHomeConfig: <any>{},
    ipBlack: <any>{},
    // 支持多语言
    supportLanguages: <Array<Record<string, any>>>[],
    // 支持法币
    supportLegalCurrency: <Array<Record<string, any>>>[],
    activeLang: '',
    activeLegal: '',
    // 是否已请求成功数据
    alreadyData: {
      hideConfig: false,
      riskIp: false
    },
    // 风控IP信息
    riskIpDisabled: <any>{},

    // TDK
    tdks: <any>{},
    // 首页成长记录
    indexGrowUpData: {
      amount: '-',
      amountData: <Array<Record<string, any>>>[],
      userCount: '-',
      symbolCount: '-'
    },
    usdcFree: false
  }),
  getters: {
    homeConfig(state) {
      return state.localeHomeConfig[getLanguage().toLowerCase()] || { announcements: [] };
    },
    // 是否隐藏信用卡买币入口
    hideCreditPage(state) {
      return state.hideConfig?.hideFiat;
    },
    // 是否隐藏合约模拟盘
    hideDemoFutures(state) {
      return state.hideConfig?.hideVirtualTrade;
    },
    // 是否是模拟盘白名单 （控制kol版本）
    demoFuturesWhite(state) {
      return state.hideConfig?.virtualTradeInfo?.white || false;
    },
    // 是否隐藏交易机器人功能
    hideStrategy(state) {
      return state.hideConfig?.hideFuturesStrategy;
    },
    // kline 订单标记配置
    klineConfig(state) {
      return state.config.klineConfig || {};
    },
    // 隐藏USDC合约
    hideUSDCFutures(state) {
      return state.hideConfig?.hideFuturesUsdc;
    },
    // 是否隐藏资产估值
    legalCurrencyMap(state) {
      const legalCurrencyMap:any = {};
      state.supportLegalCurrency.forEach((item: any) => {
        legalCurrencyMap[item.currency] = item.currencyLabel;
      });
      return legalCurrencyMap;
    }
  },
  actions: {
    async getIpBlack() {
      const res: any = await ip.ipBlackList();
      if (res.success) {
        this.ipBlack = res.data;
      }
    },
    // 接入zendesk
    initZendesk(userId: string, commonUsedCountryCode = '') {
      let  channelid = import.meta.env.MODE === 'production' ? '' : 2;

      // 波斯语言单独定制渠道
      if (i18n.global.locale.value === 'fa-IR') {
        channelid = import.meta.env.MODE === 'production' ? 3 : 4;
      }

      (function(w: any) {
        w['cbk'] = [['config', {
          locale: siteConfig.supportLanguagesMap[i18n.global.locale.value] ? siteConfig.supportLanguagesMap[i18n.global.locale.value].zc : '',
          partnerid: userId || '',
          channelid: channelid,
          params: JSON.stringify({
            language: siteConfig.supportLanguagesMap[i18n.global.locale.value] ? siteConfig.supportLanguagesMap[i18n.global.locale.value].zc : 'en',
            ae880a37a0bc41c0a252a93705bc9fa5: userId ? 'user' : 'guest',
            '17eaa0adab874bb785487722744b702f': commonUsedCountryCode === 'IR' || commonUsedCountryCode === 'BU' ? 1 : 0
          })
        }]];
        const script = document.createElement('script');
        script.async = true;
        script.id = 'zhichiScript';
        script.src = 'https://sg.sobot.com/chat/frame/v6/entrance.js?sysnum=06953b78fe4748828f65eb066d83e8c1';
        document.body.appendChild(script);
      })(window);

    },
    // 获取配置
    async getConfig(cb: any) {
      const res: any = await baseAPI.getCommonConfig();
      if (res.success) {
        this.config = res.data;
        cb && cb();
      }
    },
    // 获取配置
    async getTdk(cb: any) {
      try {
        const res: any = await axios.get(this.config.tdkUrl + i18n.global.locale.value?.toLowerCase(), { withCredentials: false });
        // 波斯语言标识比较特殊, 路经替换是不能将/fa过滤掉，做下key 拼接

        if (i18n.global.locale.value?.toLowerCase() == 'fa-ir') {
          const tdks: any = {};
          Object.keys(res.data).forEach((key: string) => {
            tdks[`/fa${key}`] = res.data[key];
          });
          this.tdks = tdks;
        } else {
          this.tdks = res.data;
        }

        cb && cb();
      } catch (e) {

      }
    },
    // 首页配置数据
    async getHomeConfig(lang?: string) {
      lang = lang || i18n.global.locale.value?.toLowerCase();
      const res: any = await $api.getIndex_config(lang);
      if (res.code === 200) {
        if (lang) {
          this.homeConfigReady[lang] = true;
          this.localeHomeConfig[lang] = res.data;
        }
      }
    },
    // 支持语言
    async getSupportLanguages() {
      const res: any = await $api.getSupportLanguages({});

      if (res.success) {
        this.supportLanguages = res.data;
      }
    },
    async getSupportLegalCurrency() {
      const res: any = await $api.getSupportLegalCurrency({});

      if (res.success) {
        // 处理 RUB ，放到第六位，后续配置加排序功能
        const list = res.data?.filter((item: any) => item.currency !== 'RUB');
        const rubItem = res.data.find((item: any) => item.currency === 'RUB');
        if (rubItem) {
          list.splice(5, 0, rubItem);
        }
        this.supportLegalCurrency = list;
      }
    },
    // 页面入口隐藏配置
    async getHideConfig(update?: false) {
      if (!update && this.alreadyData.hideConfig) return;

      const res: any = await $api.getHideConfig({});

      if (res.success) {
        this.hideConfig = res.data;
        this.alreadyData.hideConfig = true;
      }
    },
    /**
     *
     * @param ip 获取ip是否受限制
     * @param isNeedDeviceToken 是否需要等待设备指纹，限制页面需要等待设备指纹上报完成
     * @returns
     */
    async getRiskIP(ip?: string, isNeedDeviceToken?: boolean) {
      // 获取设备指纹
      if (!getCookie('d') && isNeedDeviceToken && !import.meta.env.SSR) {
        try {
          await getTdidDeviceId();
        } catch (error) {
          console.log(error);
        }
      }

      const res: any = await $api.getRiskIP({ ip, timeZone: import.meta.env.SSR ? '' : moment.tz.guess() });
      // const res: any = await $api.getRiskIP({ ip, timeZone: moment.tz.guess() });

      if (res.success) {
        this.alreadyData.riskIp = true;
        this.riskIpDisabled = res.data?.riskIpDisabled || {};
        if (res.data?.riskIpDisabled?.description) {
          localStorage.setItem('riskIp', JSON.stringify(res.data?.riskIpDisabled));
        } else {
          localStorage.removeItem('riskIp');
        }

        return !!Object.keys(res.data?.riskIpDisabled).length;
      }

      return false;
    },
    // 首页成长之旅数据
    async getGrowUpData() {
      const res = await Promise.all([baseAPI.getIndexAmount(), baseAPI.getIndexUserCount(), baseAPI.getIndexSymbolCount()]);
      res.forEach((item, index) => {
        if (item && item.success) {
          if (index === 0) {
            this.indexGrowUpData.amount = toFormat(Math.floor(item.data.amount));
            if (item.data.amountData) {
              const arr: any[] = [];
              item.data.amountData.forEach((item: any, index: number) => {
                arr.push({ t: index, c: item });
              });
              if (arr.length) {
                this.indexGrowUpData.amountData = arr;
              }
            }

          } else if (index === 1) {
            this.indexGrowUpData.userCount = toFormat(item.data.userCount);
          } else {
            this.indexGrowUpData.symbolCount = toFormat(item.data.symbolCount);
          }
        }
      });
    },
    // 首页成长之旅更新交易量
    async updateGrowUpAmount() {
      const res = await baseAPI.getIndexAmount();
      if (res.success) {
        this.indexGrowUpData.amount = toFormat(Math.floor(res.data.amount));
        if (res.data.amountData) {
          const arr: any[] = [];
          res.data.amountData.forEach((item: any, index: number) => {
            arr.push({ t: index, c: item });
          });
          if (arr.length) {
            this.indexGrowUpData.amountData = arr;
          }
        }
      }
    },
    // 首页成长之旅更新数据
    async updateGrowUpUserCount() {
      const res = await baseAPI.getIndexUserCount();
      if (res.success) {
        this.indexGrowUpData.userCount = toFormat(res.data.userCount);
      }
    },
    async getCryptoPublicKey() {
      const res = await baseAPI.GetApiPublicKey();
      if (res && res.code === 200) {
        const { init } = useCrypto();
        init(res.data);
      }
    },
    async getUsdcFree() {
      const res = await $api.getUsdcFree();
      if (res.success) {
        this.usdcFree = Array.isArray(res.data) && res.data.length > 0;
      }
    }
  }
});
